import { verifyOtp } from '@/api/otp';
import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import { displayFullMalaysiaMobile } from '@/utils/malaysiaMobile';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const lightInputClassName =
  'min-w-0 w-full rounded-sm border border-slate-300/90 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 outline-none ring-0 focus:border-slate-400 focus:ring-1 focus:ring-slate-300';

export default function Otp() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);
  const [otpCode, setOtpCode] = useState('');
  const [otpWarning, setOtpWarning] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }

  const handleSubmit = async () => {
    const trimmed = otpCode.trim();
    if (!trimmed) {
      setOtpWarning(t.otp.emptyWarning);
      return;
    }
    setOtpWarning(null);
    setIsSubmitting(true);
    try {
      const result = await verifyOtp({ customerUuid, otp: trimmed });
      if (result.ok) {
        navigate('/smokerprofile');
        return;
      }
      setOtpWarning(result.error);
    } catch {
      setOtpWarning(t.otp.verifyError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="mx-auto mt-[25vh] flex w-full max-w-full flex-col py-8">
          <SurveyBrownPanelInset>
            <p className="m-0 text-left text-[16px] font-normal leading-snug text-white/95 sm:text-[17px]">
              <span className="block">{t.otp.sentMessage}</span>
              <span className="mt-1 block font-semibold tabular-nums">
                {displayFullMalaysiaMobile(mobileLocalDigits)}
              </span>
            </p>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-6">
            <SurveyBrownPanel className="!p-4">
              <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-5 sm:gap-x-3">
                <label
                  htmlFor="otp-code"
                  className="text-left text-[16px] font-medium text-white/95 sm:text-[17px]"
                >
                  {t.otp.otpCodeLabel}
                </label>
                <input
                  id="otp-code"
                  type="text"
                  name="otpCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder=""
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`${lightInputClassName} tabular-nums tracking-wide`}
                />
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-10">
            <div className="flex flex-col items-center gap-3">
              {otpWarning != null ? <SurveyValidationAlert>{otpWarning}</SurveyValidationAlert> : null}
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                disabled={isSubmitting}
                onClick={() => void handleSubmit()}
              >
                {t.otp.submit}
              </MetallicPillButton>
            </div>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
